/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useRef, useState, useEffect, useMemo, memo } from "react";
import clsx from "clsx";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import Marker from "@/components/Googlemap/Marker";
import PolyLine from "@/components/Googlemap/Polyline";
import {
  manifest,
  // manifestDetailETA,
  manifestLine,
  // manifestLineETA,
  storageAddress,
} from "../models";
import {
  GOOGLE_API_KEY,
  GOOGLE_MAP_ID,
  GOOGLE_MAP_OPTIONS,
} from "@/configuration/globalVariable";
import { EyeMapDropdown } from "@/components/Googlemap/EyeMapDropdown";
import { Icon, LoadingOverlay } from "@/components";
import { filterDuplicateManifestLine, isUnallocatedManifest } from "../utils";
import Button from "@/components/common/Button";
// import { EyeMapDropdown } from "./EyeMapDropdown";

// Drawing mode enum
enum DrawingMode {
  NONE = "none",
  RECTANGLE = "rectangle",
  FREEFORM = "freeform",
  MOVE = "move",
}

const DrawingPanel = ({
  drawingMode,
  setDrawingMode,
  showDrawingOptions,
  setShowDrawingOptions,
  onCancelDrawing,
}: {
  drawingMode: DrawingMode;
  setDrawingMode: (mode: DrawingMode) => void;
  showDrawingOptions: boolean;
  setShowDrawingOptions: (show: boolean) => void;
  onCancelDrawing: () => void;
}) => {
  const handleEditMap = () => {
    setShowDrawingOptions(true);
    setDrawingMode(DrawingMode.MOVE);
  };

  return (
    <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-10 bg-white rounded shadow-md">
      {!showDrawingOptions ? (
        <Button
          onClick={handleEditMap}
          color="transparent"
          iconLeft={Icon.Edit}
        >
          Edit map
        </Button>
      ) : (
        <div className="flex">
          <Button
            iconLeft={() => (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M50 0L35.355 14.645H45V30h10V14.645h9.645L50 0zM14.645 35.355L0 50l14.645 14.645V55H30V45H14.645v-9.645zm70.71 0V45H70v10h15.355v9.645L100 50L85.355 35.355zM50 37.45c-6.89 0-12.55 5.661-12.55 12.551c0 6.89 5.66 12.55 12.55 12.55c6.656 0 12.114-5.296 12.48-11.866a3.5 3.5 0 0 0 .07-.684a3.5 3.5 0 0 0-.071-.7C62.104 42.739 56.65 37.45 50 37.45zm0 7c3.107 0 5.55 2.444 5.55 5.551s-2.443 5.55-5.55 5.55c-3.107 0-5.55-2.443-5.55-5.55c0-3.107 2.443-5.55 5.55-5.55zM45 70v15.355h-9.645L50 100l14.645-14.645H55V70H45z"
                  fill="var(--color-neutral-50)"
                />
              </svg>
            )}
            onClick={() => setDrawingMode(DrawingMode.MOVE)}
            title="Move"
            color="transparent"
            className={clsx(
              drawingMode === DrawingMode.MOVE && "bg-neutral-20",
              "rounded-none"
            )}
          ></Button>
          <Button
            iconLeft={() => (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.55 15C5.662 15 0 20.661 0 27.55c0 6.017 4.317 11.096 10 12.286v20.428c-5.683 1.19-10 6.27-10 12.287C0 79.44 5.661 85.1 12.55 85.1c6.047 0 11.09-4.374 12.241-10.1h50.455c1.152 5.732 6.253 10.1 12.305 10.1c6.65 0 12.105-5.288 12.478-11.852a3.5 3.5 0 0 0 .07-.697a3.5 3.5 0 0 0-.07-.697C99.703 66.117 95.495 61.356 90 60.246V39.854c5.495-1.11 9.703-5.87 10.03-11.606a3.5 3.5 0 0 0 .07-.697a3.5 3.5 0 0 0-.07-.697C99.655 20.29 94.201 15 87.55 15c-6.016 0-11.096 4.317-12.286 10H24.77c-1.19-5.676-6.209-10-12.22-10zm0 7c3.107 0 5.55 2.444 5.55 5.55c0 3.107-2.443 5.55-5.55 5.55C9.445 33.1 7 30.657 7 27.55C7 24.445 9.444 22 12.55 22zm75 0c3.107 0 5.55 2.444 5.55 5.55c0 3.107-2.443 5.55-5.55 5.55c-3.106 0-5.55-2.443-5.55-5.55c0-3.106 2.444-5.55 5.55-5.55zM24.218 32h51.62A12.678 12.678 0 0 0 83 39.225v21.65A12.684 12.684 0 0 0 75.875 68h-51.7A12.64 12.64 0 0 0 17 60.838V39.262A12.638 12.638 0 0 0 24.217 32zM12.55 67c3.106 0 5.549 2.444 5.549 5.55c0 3.107-2.443 5.55-5.55 5.55C9.445 78.1 7 75.657 7 72.55C7 69.445 9.444 67 12.55 67zm75 0c3.106 0 5.549 2.444 5.549 5.55c0 3.107-2.443 5.55-5.55 5.55c-3.106 0-5.55-2.443-5.55-5.55c0-3.106 2.444-5.55 5.55-5.55z"
                  fill="var(--color-neutral-50)"
                />
              </svg>
            )}
            onClick={() => setDrawingMode(DrawingMode.RECTANGLE)}
            title="Rectangle"
            color="transparent"
            className={clsx(
              drawingMode === DrawingMode.RECTANGLE && "bg-neutral-20",
              "rounded-none"
            )}
          ></Button>
          <Button
            iconLeft={() => (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M32.5 10.95c-6.89 0-12.55 5.66-12.55 12.55c0 4.02 1.935 7.613 4.91 9.916L14.815 54.172a12.354 12.354 0 0 0-2.316-.223C5.61 53.95-.05 59.61-.05 66.5c0 6.89 5.66 12.55 12.55 12.55c5.13 0 9.54-3.151 11.463-7.603l51.277 7.71c1.232 5.629 6.281 9.894 12.26 9.894c6.656 0 12.114-5.297 12.48-11.867a3.5 3.5 0 0 0 .07-.684a3.5 3.5 0 0 0-.071-.7c-.375-6.562-5.829-11.85-12.479-11.85c-.134 0-.264.015-.396.019L80.242 43.05c3.275-2.127 5.509-5.746 5.738-9.867a3.5 3.5 0 0 0 .07-.684a3.5 3.5 0 0 0-.071-.7c-.375-6.562-5.829-11.85-12.479-11.85c-5.062 0-9.452 3.06-11.43 7.415l-17.082-4.517a3.5 3.5 0 0 0-.01-.047c-.374-6.563-5.828-11.852-12.478-11.852zm0 7c3.107 0 5.55 2.443 5.55 5.55c0 3.107-2.443 5.55-5.55 5.55c-3.107 0-5.55-2.443-5.55-5.55c0-3.107 2.443-5.55 5.55-5.55zm41 9c3.107 0 5.55 2.443 5.55 5.55c0 3.107-2.443 5.55-5.55 5.55c-3.107 0-5.55-2.443-5.55-5.55c0-3.107 2.443-5.55 5.55-5.55zm-30.137 2.708l17.739 4.69C62.007 40.37 67.239 45.05 73.5 45.05l.033-.002l6.92 21.092a12.688 12.688 0 0 0-4.705 6.015l-50.916-7.654a12.611 12.611 0 0 0-3.787-7.13l10.342-21.378c.368.033.737.057 1.113.057c4.652 0 8.71-2.592 10.863-6.393zM12.5 60.95c3.107 0 5.55 2.444 5.55 5.551s-2.443 5.55-5.55 5.55c-3.107 0-5.55-2.443-5.55-5.55c0-3.107 2.443-5.55 5.55-5.55zm75 10c3.107 0 5.55 2.444 5.55 5.551s-2.443 5.55-5.55 5.55c-3.107 0-5.55-2.443-5.55-5.55c0-3.107 2.443-5.55 5.55-5.55z"
                  fill="var(--color-neutral-50)"
                />
              </svg>
            )}
            onClick={() => setDrawingMode(DrawingMode.FREEFORM)}
            title="Freeform"
            color="transparent"
            className={clsx(
              drawingMode === DrawingMode.FREEFORM && "bg-neutral-20",
              "rounded-none"
            )}
          ></Button>
          <Button
            iconLeft={() => (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
            onClick={onCancelDrawing}
            title="Cancel"
            color="transparent"
            className={clsx("rounded-none")}
          ></Button>
        </div>
      )}
    </div>
  );
};

interface MapProps {
  children?: any;
  className?: any;
  center?: any;
  zoom?: any;
  allMarkerlatlng?: any;
  onLoad?: (map: google.maps.Map) => void;
}
const Map = ({
  children,
  className,
  center,
  zoom,
  allMarkerlatlng,
  onLoad,
}: MapProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [map, setMap] = useState<google.maps.Map>();

  useEffect(() => {
    if (ref.current && !map) {
      const m = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        mapId: GOOGLE_MAP_ID,
      });

      setMap(m);
      if (onLoad) {
        onLoad(m);
      }
    }
    // eslint-disable-next-line
  }, [ref, map]);

  useEffect(() => {
    map?.setOptions({ center, zoom });
    // eslint-disable-next-line
  }, [center, zoom]);

  useEffect(() => {
    if (map) {
      if (allMarkerlatlng && allMarkerlatlng.length > 1) {
        const latlngbounds = new google.maps.LatLngBounds();
        for (let i = 0; i < allMarkerlatlng.length; i++) {
          latlngbounds.extend(allMarkerlatlng[i]);
        }
        map.fitBounds(latlngbounds);
      } else {
        map.setOptions({ center, zoom });
      }
    }
    // eslint-disable-next-line
  }, [allMarkerlatlng, map]);

  return (
    <>
      <div ref={ref} className={clsx("w-full h-full g-map", className)} />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          return React.cloneElement(child, { map } as any);
        }
      })}
    </>
  );
};

const render = (status: Status) => {
  switch (status) {
    case Status.LOADING:
      return (
        <div className="w-full h-full flex items-center justify-center">
          Map is loading.....
        </div>
      );
    case Status.FAILURE:
      return (
        <div className="w-full h-full flex items-center justify-center">
          Map loaded error.
        </div>
      );
    default:
      return (
        <div className="w-full h-full flex items-center justify-center">
          Loading.....
        </div>
      );
  }
};

interface Props {
  isFromOptimize?: boolean | null;
  storageAddress: storageAddress;
  manifests: manifest[];
  routesLoading?: boolean;
  onMoveRoute?: (mnfl: manifestLine, currentManifest: manifest) => void;
  manifestIdsCanMove?: string[]; // leave undefined to enable all
  onLoad?: (map: google.maps.Map) => void;
  onSelectManifestLines?: (manifestLines: manifestLine[]) => void;
}

export const GoogleMap = memo(
  ({
    isFromOptimize,
    storageAddress,
    manifests,
    routesLoading,
    onMoveRoute,
    manifestIdsCanMove,
    onLoad,
    onSelectManifestLines,
  }: Props) => {
    const [routesDisplay, setRoutesDisplay] = useState<manifest[]>(
      manifests || []
    );
    const [drawingMode, setDrawingMode] = useState<DrawingMode>(
      DrawingMode.NONE
    );
    const [showDrawingOptions, setShowDrawingOptions] =
      useState<boolean>(false);
    const [drawingManager, setDrawingManager] =
      useState<google.maps.drawing.DrawingManager | null>(null);
    const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(
      null
    );

    const center = useMemo(() => {
      return {
        lat: storageAddress?.latitude || -25.344,
        lng: storageAddress?.longitude || 131.031,
      };
      // eslint-disable-next-line
    }, [storageAddress]);

    const zoom = useMemo(
      () => (storageAddress?.latitude ? 11 : 4.6),
      [storageAddress]
    );

    const ParseETAPolyline = (polyline: string) => {
      const polylines = JSON.parse(polyline)[0];

      if (polylines) {
        const decode = google.maps.geometry.encoding.decodePath(polylines);
        const listposition = [];

        for (let c = 0; c < decode.length; c++) {
          const coords = new google.maps.LatLng(
            decode[c].lat() / 10,
            decode[c].lng() / 10
          );
          listposition.push(coords);
        }
        return listposition;
      }
    };

    const isRenderLine = useMemo(
      () => Boolean(routesDisplay && routesDisplay.length),
      [routesDisplay]
    );

    const allMarkerlatlng = useMemo(() => {
      if (manifests?.length) {
        const latlng = [
          new google.maps.LatLng(
            storageAddress?.latitude || 0,
            storageAddress?.longitude || 0
          ),
        ];

        manifests.map((manifest: manifest) => {
          if (
            manifest &&
            manifest.manifestLines &&
            manifest.manifestLines.length &&
            (manifest.polylines || isUnallocatedManifest(manifest))
          ) {
            manifest.manifestLines.map((manifestLine: manifestLine) => {
              return manifestLine?.latitude
                ? latlng.push(
                    new google.maps.LatLng(
                      manifestLine?.latitude || 0,
                      manifestLine?.longitude || 0
                    )
                  )
                : null;
            });
          }

          return manifest;
        });

        return latlng;
      }
      // eslint-disable-next-line
    }, [manifests]);

    const handleShowHideRoute = (route: manifest) => {
      setRoutesDisplay(
        routesDisplay.some((rd) => rd.id === route.id)
          ? routesDisplay.filter((rd) => rd.id !== route.id)
          : routesDisplay.concat(
              manifests.filter((mfETA: manifest) => mfETA.id === route.id)
            )
      );
    };

    const isManifestCanMove = (manifestId: string) => {
      return !manifestIdsCanMove || manifestIdsCanMove.includes(manifestId);
    };

    useEffect(() => {
      const newManifests = manifests
        ? manifests.map((mf) => {
            if (mf) {
              return {
                ...mf,
                manifestLine: filterDuplicateManifestLine(mf?.manifestLines),
              };
            }
            return mf;
          })
        : [];
      setRoutesDisplay(newManifests);
    }, [manifests]);

    // Handle map loading
    const handleMapLoad = (map: google.maps.Map) => {
      setMapInstance(map);
      if (onLoad) {
        onLoad(map);
      }
    };

    // Initialize drawing manager
    useEffect(() => {
      if (mapInstance && !drawingManager) {
        // Initialize drawing manager
        const manager = new google.maps.drawing.DrawingManager({
          drawingMode: null,
          drawingControl: false,
          rectangleOptions: {
            strokeColor: "#0f5584",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: "#0f5584",
            fillOpacity: 0.35,
            editable: false,
          },
          polygonOptions: {
            strokeColor: "#0f5584",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: "#0f5584",
            fillOpacity: 0.35,
            editable: false,
          },
        });

        manager.setMap(mapInstance);
        setDrawingManager(manager);
      }
    }, [mapInstance]);

    // Set up event listeners that have access to current state
    useEffect(() => {
      if (drawingManager) {
        // Clear previous listeners
        google.maps.event.clearListeners(drawingManager, "rectanglecomplete");
        google.maps.event.clearListeners(drawingManager, "polygoncomplete");

        // Add event listeners with access to current manifests
        google.maps.event.addListener(
          drawingManager,
          "rectanglecomplete",
          handleShapeComplete
        );
        google.maps.event.addListener(
          drawingManager,
          "polygoncomplete",
          handleShapeComplete
        );
      }
    }, [drawingManager, manifests]); // Re-add listeners when manifests change

    // Update drawing mode
    useEffect(() => {
      if (drawingManager) {
        switch (drawingMode) {
          case DrawingMode.RECTANGLE:
            drawingManager.setDrawingMode(
              google.maps.drawing.OverlayType.RECTANGLE
            );
            if (mapInstance) {
              mapInstance.setOptions({ draggableCursor: "crosshair" });
            }
            break;
          case DrawingMode.FREEFORM:
            drawingManager.setDrawingMode(
              google.maps.drawing.OverlayType.POLYGON
            );
            if (mapInstance) {
              mapInstance.setOptions({ draggableCursor: "crosshair" });
            }
            break;
          case DrawingMode.MOVE:
            drawingManager.setDrawingMode(null);
            if (mapInstance) {
              mapInstance.setOptions({
                draggableCursor: "grab",
                draggingCursor: "grabbing",
              });
            }
            break;
          default:
            drawingManager.setDrawingMode(null);
            if (mapInstance) {
              mapInstance.setOptions({
                draggableCursor: "",
                draggingCursor: "",
              });
            }
            break;
        }
      }
    }, [drawingMode, drawingManager, mapInstance]);

    // Clean up drawing manager on unmount
    useEffect(() => {
      return () => {
        if (drawingManager) {
          google.maps.event.clearInstanceListeners(drawingManager);
          drawingManager.setMap(null);
        }
      };
    }, [drawingManager]);

    // Handle shape completion
    const handleShapeComplete = (
      shape: google.maps.Rectangle | google.maps.Polygon
    ) => {
      // Reset drawing mode
      setDrawingMode(DrawingMode.NONE);

      // Get markers within shape
      const selectedLines: manifestLine[] = [];

      // Loop through all manifests and their lines
      if (manifests && manifests.length > 0) {
        manifests.forEach((manifest) => {
          if (manifest.manifestLines && manifest.manifestLines.length > 0) {
            manifest.manifestLines.forEach((line) => {
              if (line.latitude && line.longitude) {
                const position = new google.maps.LatLng(
                  line.latitude,
                  line.longitude
                );

                let isInside = false;
                if (shape instanceof google.maps.Rectangle) {
                  const bounds = shape.getBounds();
                  isInside = bounds?.contains(position) || false;
                } else if (shape instanceof google.maps.Polygon) {
                  isInside = google.maps.geometry.poly.containsLocation(
                    position,
                    shape
                  );
                }

                if (isInside) {
                  selectedLines.push(line);
                }
              }
            });
          }
        });
      }

      if (selectedLines.length > 0 && onSelectManifestLines) {
        onSelectManifestLines(selectedLines);
        setTimeout(() => {
          shape.setMap(null);
          setShowDrawingOptions(false);
        }, 100);
      } else {
        setTimeout(() => {
          shape.setMap(null);
        }, 100);
      }
    };

    // Handle cancel drawing
    const handleCancelDrawing = () => {
      setDrawingMode(DrawingMode.NONE);
      setShowDrawingOptions(false);

      // Clear any active shapes
      if (drawingManager) {
        drawingManager.setDrawingMode(null);
      }
    };

    return GOOGLE_API_KEY ? (
      <div className="ralative w-full h-full">
        {routesLoading && <LoadingOverlay />}
        <Wrapper render={render} {...GOOGLE_MAP_OPTIONS}>
          <Map
            center={center}
            zoom={zoom}
            allMarkerlatlng={allMarkerlatlng}
            onLoad={handleMapLoad}
          >
            {storageAddress?.latitude && (
              <Marker
                isFromOptimize={isFromOptimize}
                position={{
                  lat: storageAddress?.latitude || 0,
                  lng: storageAddress?.longitude || 0,
                }}
                data={storageAddress}
                isStore
              />
            )}

            {isRenderLine && routesDisplay.length
              ? routesDisplay.map(
                  (manifest: manifest) =>
                    (manifest.polylines || isUnallocatedManifest(manifest)) &&
                    manifest.manifestLines.map((mnfl: manifestLine) => {
                      return mnfl?.latitude && mnfl?.longitude ? (
                        <Marker
                          isFromOptimize={isFromOptimize}
                          key={mnfl.id}
                          position={{
                            lat: mnfl?.latitude || 0,
                            lng: mnfl?.longitude || 0,
                          }}
                          data={mnfl}
                          manifest={manifest}
                          onMoveRoute={
                            onMoveRoute && isManifestCanMove(manifest.id)
                              ? onMoveRoute
                              : undefined
                          }
                        />
                      ) : (
                        <></>
                      );
                    })
                )
              : null}

            {isRenderLine &&
              routesDisplay.length &&
              routesDisplay.map(
                (manifest, index) =>
                  manifest.polylines && (
                    <PolyLine
                      key={index + manifest.id}
                      path={ParseETAPolyline(manifest.polylines)}
                      strokeColor={manifest?.route?.colour}
                    />
                  )
              )}
          </Map>
        </Wrapper>
        {onSelectManifestLines && (
          <DrawingPanel
            drawingMode={drawingMode}
            setDrawingMode={setDrawingMode}
            showDrawingOptions={showDrawingOptions}
            setShowDrawingOptions={setShowDrawingOptions}
            onCancelDrawing={handleCancelDrawing}
          />
        )}
        <div className="absolute bottom-3 left-3 z-10">
          <EyeMapDropdown
            manifests={manifests}
            onShowHideClick={handleShowHideRoute}
            routesActiveShow={routesDisplay}
          />
        </div>
      </div>
    ) : null;
  }
);
